import { useState } from "react";
import { Combobox, ComboboxInput, ComboboxOption, ComboboxOptions } from "@headlessui/react";
import { Check, ChevronDown } from "lucide-react";
import { useFormField } from "../../hooks/useFormField";
import { useFormStore } from "../../store/formStore";
import { useIsMobile } from "../../hooks/useIsMobile";
import { FieldHint } from "../FieldHint";
import { inputClass, labelClass, resolveFieldState } from "./fieldStyles";
import type { IntakeFormData } from "../../types/form";
import type { useStepValidation } from "../../hooks/useStepValidation";

type StringField = {
  [K in keyof IntakeFormData]: IntakeFormData[K] extends string ? K : never;
}[keyof IntakeFormData];

export interface SearchOption {
  value: string;
  label: string;
}

interface SearchableSelectInputProps {
  field: StringField;
  label: string;
  validation: ReturnType<typeof useStepValidation>;
  options: SearchOption[];
  required?: boolean;
  placeholder?: string;
  /** When the selected value equals "Other", shows a free-text input bound to this field. */
  otherField?: StringField;
  otherPlaceholder?: string;
  otherMaxLength?: number;
  containerClassName?: string;
}

/**
 * Typeahead combobox for string fields. Filters options as the user types and
 * supports an "Other" value that reveals a free-text input bound to `otherField`.
 * Built on Headless UI Combobox — keyboard nav, ARIA, and focus management handled by the library.
 *
 * Usage:
 *   <SearchableSelectInput field="priorMedicalCarrier" label="Current Carrier"
 *     options={MEDICAL_CARRIERS} otherField="priorMedicalCarrierOther"
 *     validation={validation} required />
 */
export function SearchableSelectInput({
  field,
  label,
  validation,
  options,
  required,
  placeholder = "Search or select...",
  otherField,
  otherPlaceholder = "Specify...",
  otherMaxLength,
  containerClassName,
}: SearchableSelectInputProps) {
  const f = useFormField(field, validation);
  const otherValue = useFormStore((s) =>
    otherField ? String(s.data[otherField] ?? "") : ""
  );
  const setField = useFormStore((s) => s.setField);
  const isMobile = useIsMobile();
  const [query, setQuery] = useState("");

  const filtered = options.filter((opt) =>
    opt.label.toLowerCase().includes(query.toLowerCase())
  );

  const selectedOption = options.find((o) => o.value === f.value);
  const state = resolveFieldState({
    error: f.error,
    isMismatch: !!f.mismatch,
    isPrefilled: f.isPrefilled,
  });

  return (
    <div className={containerClassName}>
      <label className={labelClass()} htmlFor={`${field}-input`}>
        {label}
        {required && <span className="text-danger" aria-hidden="true">&nbsp;*</span>}
      </label>

      {isMobile ? (
        <div className="relative focus-within:ring-2 focus-within:ring-brand-green-50 rounded-lg">
          <div
            aria-hidden="true"
            className={`${inputClass(state)} ${selectedOption ? "pl-10 text-gray-900" : "text-gray-400"} pr-8 pointer-events-none`}
          >
            {selectedOption && (
              <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-brand-green">
                <Check size={16} />
              </span>
            )}
            <span className="block truncate">{selectedOption?.label ?? placeholder}</span>
            <span className="absolute inset-y-0 right-0 flex items-center pr-3">
              <ChevronDown size={16} className="text-gray-400" />
            </span>
          </div>
          <select
            id={`${field}-input`}
            aria-label={label}
            value={f.value}
            onChange={(e) => {
              const next = e.target.value;
              f.setValue(next as IntakeFormData[typeof field]);
              if (otherField && next !== "Other") {
                setField(otherField, "" as IntakeFormData[typeof otherField]);
              }
              f.onBlur();
            }}
            className="absolute inset-0 opacity-0 cursor-pointer text-base w-full"
          >
            <option value="">{placeholder}</option>
            {options.map((opt) => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>
      ) : (
        <div className="relative">
          <Combobox
            immediate
            value={f.value}
            onChange={(val: string | null) => {
              const next = val ?? "";
              f.setValue(next as IntakeFormData[typeof field]);
              if (otherField && next !== "Other") {
                setField(otherField, "" as IntakeFormData[typeof otherField]);
              }
              f.onBlur();
            }}
            onClose={() => setQuery("")}
          >
            <ComboboxInput
              id={`${field}-input`}
              autoComplete="off"
              displayValue={() => selectedOption?.label ?? ""}
              onChange={(e) => setQuery(e.target.value)}
              onBlur={f.onBlur}
              placeholder={placeholder}
              className={inputClass(state)}
            />
            {filtered.length > 0 && (
              <ComboboxOptions
                anchor="bottom start"
                className="z-50 max-h-56 w-[var(--input-width)] overflow-auto rounded-lg bg-white py-1 text-sm
                  shadow-lg ring-1 ring-black/5 focus:outline-none [--anchor-gap:4px] [--anchor-max-height:50vh]"
              >
                {filtered.map((opt) => (
                  <ComboboxOption
                    key={opt.value}
                    value={opt.value}
                    className="relative cursor-pointer select-none py-2.5 pl-10 pr-4 text-gray-900
                      data-[focus]:bg-brand-green-10 data-[focus]:text-brand-green
                      data-[selected]:bg-brand-green-10 data-[selected]:text-brand-green data-[selected]:font-semibold"
                  >
                    {({ selected }) => (
                      <>
                        {selected && (
                          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-brand-green">
                            <Check size={16} aria-hidden="true" />
                          </span>
                        )}
                        {opt.label}
                      </>
                    )}
                  </ComboboxOption>
                ))}
              </ComboboxOptions>
            )}
          </Combobox>
        </div>
      )}

      <FieldHint error={f.error} isPrefilled={f.isPrefilled} mismatch={f.mismatch} />

      {f.value === "Other" && otherField && (
        <input
          type="text"
          aria-label={`${label} — specify other`}
          value={otherValue}
          placeholder={otherPlaceholder}
          maxLength={otherMaxLength}
          className={`${inputClass()} mt-2`}
          onChange={(e) =>
            setField(otherField, e.target.value as IntakeFormData[typeof otherField])
          }
          onBlur={f.onBlur}
        />
      )}
    </div>
  );
}
